import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

/*
 * The website's own visit counter — owner ask, 2026-08-10.
 *
 * **Renders nothing.** It sits in `PublicLayout` and reports each page the
 * visitor lands on to `/api/public/traffic/view`, which hashes them into a
 * daily-rotating handle and forgets everything else. No cookie is set, no
 * identifier is stored, and nothing here needs consent — which is the entire
 * reason it exists alongside Google Analytics rather than being replaced by it.
 *
 * **Marketing pages only — the four in `MARKETING_PATHS` and nothing else.**
 *
 * Sitting inside `PublicLayout` is not enough on its own, and finding that out
 * took looking at the screen rather than at the code: the four auth screens
 * moved into this layout on 2026-08-07, so that the login page would stop being
 * a dead end. `/login` was therefore the second-most-read page on this counter's
 * very first afternoon — and it was a cleaner signing in. Staff arriving at work
 * is not website traffic, and a figure inflated that way looks completely
 * plausible, which is the worst kind of wrong this project deals in.
 *
 * **An allowlist rather than a list of exclusions**, because the two fail in
 * opposite directions. An auth screen nobody remembered to exclude quietly
 * corrupts the numbers; a marketing page nobody remembered to add is merely
 * missing, and missing gets noticed. **Add a line here when the public site
 * gains a page.**
 *
 * ---
 *
 * **`sendBeacon` rather than `fetch`, and the difference is real.** A visitor
 * who lands on a page and immediately clicks away is exactly the visit most
 * worth counting, and a `fetch` still in flight when the page unloads is
 * cancelled by the browser. `sendBeacon` hands the request to the browser to
 * deliver on its own schedule, and it survives the page going away. `fetch` with
 * `keepalive` is the fallback for anything that lacks it.
 *
 * **Nothing here can surface an error.** A counter is not worth a broken page,
 * so every failure path ends in silence — no toast, no console noise on a
 * customer's screen, no retry. The server logs what it could not record.
 */

const ENDPOINT = '/api/public/traffic/view';

/** The public site's own pages. Not the auth screens that share its shell. */
const MARKETING_PATHS = new Set(['/', '/services', '/contact', '/careers']);

export function PageViewTracker() {
  const { pathname } = useLocation();

  /*
   * **React 18's StrictMode runs every effect twice in development**, and
   * without this the local numbers would be exactly double the real ones — the
   * kind of wrong that looks plausible enough to go unnoticed until somebody
   * compares against Google. Also covers a re-render that changes nothing about
   * the path.
   */
  const lastSent = useRef<string | null>(null);

  useEffect(() => {
    if (!MARKETING_PATHS.has(pathname)) return;
    if (lastSent.current === pathname) return;
    lastSent.current = pathname;

    const body = JSON.stringify({
      // The path only. A query string is rejected by the server on purpose —
      // it is where a mis-built form leaks an email address into a table that
      // must never hold one.
      path: pathname,
      // Empty on a direct visit, which the server stores as null rather than as
      // a blank string.
      referrer: document.referrer || undefined,
    });

    try {
      if (typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
        return;
      }

      void fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {
        // Silence on purpose — see the file comment.
      });
    } catch {
      // Same. A blocked beacon, an extension refusing the request, an offline
      // browser: all of them mean one uncounted visit and nothing else.
    }
  }, [pathname]);

  return null;
}
